import { useMsal } from '@azure/msal-react';
import {
    Button,
    Checkbox,
    Combobox,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    Input,
    makeStyles,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    Option,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Textarea,
    tokens
} from '@fluentui/react-components';
import { Delete24Regular, Dismiss24Regular, Edit24Regular, Save24Regular } from '@fluentui/react-icons';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useProject } from '../../context/ProjectContext';
import type { ProjectUserInfo } from '../../models/ProjectUserInfo';
import type { ProjektItem } from '../../models/ProjektItem';
import type { ProjektmitarbeiterItem } from '../../models/ProjektmitarbeiterItem';
import type { ZahlungsplanItem } from '../../models/ZahlungsplanItem';
import { DataService } from '../../services/dataService';
import useConfigStore from '../../store/config';

const useStyles = makeStyles({
    container: {
        padding: tokens.spacingHorizontalL,
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: tokens.spacingVerticalL,
    },
    headerTitle: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: 700,
        color: tokens.colorNeutralForeground1,
    },
    categorySection: {
        display: 'grid',
        gridTemplateColumns: '250px 1fr',
        gap: tokens.spacingHorizontalL,
        marginBottom: tokens.spacingVerticalL,
        paddingBottom: tokens.spacingVerticalL,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    categoryHeader: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: 700,
        color: tokens.colorBrandForeground1,
        paddingTop: '2px',
    },
    fieldsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '5px',
    },
    fieldBox: {
        display: 'grid',
        gridTemplateColumns: '250px 1fr',
        gap: tokens.spacingHorizontalM,
        alignItems: 'start',
        paddingBottom: 0,
    },
    fieldLabel: {
        fontWeight: 600,
        fontSize: '11px',
        color: tokens.colorNeutralForeground2,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        paddingTop: '4px',
    },
    fieldValue: {
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground1,
        wordWrap: 'break-word',
    },
    emptyValue: {
        color: tokens.colorNeutralForeground3,
        fontStyle: 'italic',
    },
    booleanValue: {
        padding: '2px 6px',
        borderRadius: '2px',
        fontSize: '11px',
        fontWeight: 600,
        display: 'inline-block',
        width: 'fit-content',
    },
    booleanTrue: {
        backgroundColor: tokens.colorStatusSuccessBackground2,
        color: tokens.colorStatusSuccessForeground1,
    },
    booleanFalse: {
        backgroundColor: tokens.colorNeutralBackground3,
        color: tokens.colorNeutralForeground3,
    },
    inputField: {
        width: '100%',
        fontSize: tokens.fontSizeBase300,
        height: '32px',
    },
    largeTextarea: {
        width: '100%',
        fontSize: tokens.fontSizeBase300,
        minHeight: '120px',
        resize: 'none',
    },
    tableContainer: {
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: '4px',
        overflow: 'hidden',
        flex: 1,
        minWidth: '200px',
    },
    table: {
        width: '100%',
        fontSize: tokens.fontSizeBase300,
    },
    tableHeaderRow: {
        backgroundColor: tokens.colorNeutralBackground3,
    },
    footerBar: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        marginTop: tokens.spacingVerticalXXL,
        paddingTop: tokens.spacingVerticalL,
        borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
        gap: tokens.spacingHorizontalM,
    },
    placeholder: {
        padding: tokens.spacingVerticalXL,
        textAlign: 'center',
        color: tokens.colorNeutralForeground2,
    },
});

interface EckdatenFinanziellsProps {
    project: ProjektItem | null;
}

interface EditedValues {
    [key: string]: string | boolean | number | undefined;
}

interface PermissionConfig {
    'Rolle Administratoren'?: 'r' | 'rw';
    'Rolle Hochschulleitung'?: 'r' | 'rw';
    'Rolle Controlling'?: 'r' | 'rw';
    'Rolle Accounting'?: 'r' | 'rw';
    'Rolle Vertragskoordination'?: 'r' | 'rw';
    'Rolle Forschungsservice'?: 'r' | 'rw';
    'Rolle Rektorat'?: 'r' | 'rw';
    isBenutzerProjektLeiter?: 'r' | 'rw';
    isBenutzerProjektleiterAssistent?: 'r' | 'rw';
    departmentHead?: 'r' | 'rw';
    isRegularUser?: 'r' | 'rw';
}

interface FieldConfig {
    key: string;
    label: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value?: any;
    isBoolean?: boolean;
    isTextarea?: boolean;
    isLargeTextarea?: boolean;
    editable?: boolean;
}

const initializeEditedValues = (project: ProjektItem | null): EditedValues => {
    if (!project) return {};
    return {
        kostentraegerNummer: project.kostentraegerNummer || '',
        stammKostentraegerNummer: project.stammKostentraegerNummer || '',
        kostenstellenNummer: project.kostenstellenNummer || '',
        kostenAusBMD: project.kostenAusBMD || false,
        ustPflichtig: project.ustPflichtig || false,
        freigabeControlling: project.freigabeControlling || false,
        zustaendigerMaControlling: project.zustaendigerMaControllingId ? String(project.zustaendigerMaControllingId) : '',
        ausfinanziertAsBoolean: project.ausfinanziertAsBoolean || false,
        kommentarFinanzdaten: project.kommentarFinanzdaten || '',
        // Financial Data - Contract Section
        auftragsummeNetto: project.auftragsummeNetto || '',
        foerdersumme: project.foerdersumme || '',
        eigenmittel: project.eigenmittel || '',
        foerderquoteProzent: project.foerderquoteProzent || '',
        // Financial Data - IMC Calculation Section
        kostenPlan: project.kostenPlan || '',
        foerdersummeTotal: project.foerdersummeTotal || '',
        deckungsbeitragPlan: project.deckungsbeitragPlan || '',
        foerderquoteProzentPlan: project.foerderquoteProzentPlan || '',
        // Financial Data - Consortium Section
        finanzdatenKonsortiumVolumen: project.finanzdatenKonsortiumVolumen || '',
        finanzdatenKonsortiumDavonIMC: project.finanzdatenKonsortiumDavonIMC || '',
        finanzdatenKonsortiumIMCAnteil: project.finanzdatenKonsortiumIMCAnteil || '',
    };
};

const canEditField = (
    fieldPermissions: PermissionConfig | undefined,
    userRole: string | null,
    project: ProjektItem | null,
    userInfo: ProjectUserInfo | null
): boolean => {
    if (!fieldPermissions || !project) return false;

    // Check if user is a project manager
    if (project.isBenutzerProjektLeiter && fieldPermissions.isBenutzerProjektLeiter === 'rw') {
        return true;
    }

    // Check if user is a project manager assistant
    if (project.isBenutzerProjektleiterAssistent && fieldPermissions.isBenutzerProjektleiterAssistent === 'rw') {
        return true;
    }

    // Check if user is department head (if departmentLeiterID matches current user)
    if (userInfo && project.departmentLeiterID === userInfo.userId && fieldPermissions.departmentHead === 'rw') {
        return true;
    }

    // Check role-based permissions
    if (userRole === 'Administratoren' && fieldPermissions['Rolle Administratoren'] === 'rw') {
        return true;
    }

    if (userRole === 'Hochschulleitung' && fieldPermissions['Rolle Hochschulleitung'] === 'rw') {
        return true;
    }

    if (userRole === 'Controlling' && fieldPermissions['Rolle Controlling'] === 'rw') {
        return true;
    }

    if (userRole === 'Accounting' && fieldPermissions['Rolle Accounting'] === 'rw') {
        return true;
    }

    if (userRole === 'Vertragskoordination' && fieldPermissions['Rolle Vertragskoordination'] === 'rw') {
        return true;
    }

    if (userRole === 'Forschungsservice' && fieldPermissions['Rolle Forschungsservice'] === 'rw') {
        return true;
    }

    if (userRole === 'Rektorat' && fieldPermissions['Rolle Rektorat'] === 'rw') {
        return true;
    }

    return false;
};

const canViewField = (
    fieldPermissions: PermissionConfig | undefined,
    userRole: string | null,
    project: ProjektItem | null,
    userInfo: ProjectUserInfo | null
): boolean => {
    if (!fieldPermissions || !project) return false;

    // Check if user is a project manager (read or write)
    if (project.isBenutzerProjektLeiter &&
        (fieldPermissions.isBenutzerProjektLeiter === 'rw' || fieldPermissions.isBenutzerProjektLeiter === 'r')) {
        return true;
    }

    // Check if user is a project manager assistant (read or write)
    if (project.isBenutzerProjektleiterAssistent &&
        (fieldPermissions.isBenutzerProjektleiterAssistent === 'rw' || fieldPermissions.isBenutzerProjektleiterAssistent === 'r')) {
        return true;
    }

    // Check if user is department head (if departmentLeiterID matches current user)
    if (userInfo && project.departmentLeiterID === userInfo.userId &&
        (fieldPermissions.departmentHead === 'rw' || fieldPermissions.departmentHead === 'r')) {
        return true;
    }

    // Check role-based permissions (read or write)
    if (userRole === 'Administratoren' && (fieldPermissions['Rolle Administratoren'] === 'rw' || fieldPermissions['Rolle Administratoren'] === 'r')) {
        return true;
    }

    if (userRole === 'Hochschulleitung' && (fieldPermissions['Rolle Hochschulleitung'] === 'rw' || fieldPermissions['Rolle Hochschulleitung'] === 'r')) {
        return true;
    }

    if (userRole === 'Controlling' && (fieldPermissions['Rolle Controlling'] === 'rw' || fieldPermissions['Rolle Controlling'] === 'r')) {
        return true;
    }

    if (userRole === 'Accounting' && (fieldPermissions['Rolle Accounting'] === 'rw' || fieldPermissions['Rolle Accounting'] === 'r')) {
        return true;
    }

    if (userRole === 'Vertragskoordination' && (fieldPermissions['Rolle Vertragskoordination'] === 'rw' || fieldPermissions['Rolle Vertragskoordination'] === 'r')) {
        return true;
    }

    if (userRole === 'Forschungsservice' && (fieldPermissions['Rolle Forschungsservice'] === 'rw' || fieldPermissions['Rolle Forschungsservice'] === 'r')) {
        return true;
    }

    if (userRole === 'Rektorat' && (fieldPermissions['Rolle Rektorat'] === 'rw' || fieldPermissions['Rolle Rektorat'] === 'r')) {
        return true;
    }

    return false;
};

const EckdatenFinanzielles: React.FC<EckdatenFinanziellsProps> = ({ project }) => {
    const styles = useStyles();
    const { selectedRole } = useConfigStore((state) => state.roleConfig);
    const { userInfo: configUserInfo } = useConfigStore((state) => state.userConfig);
    const { instance, accounts } = useMsal();
    const { refreshProject } = useProject();
    const [editedValues, setEditedValues] = useState<EditedValues>(() => initializeEditedValues(project));
    const [isEditing, setIsEditing] = useState(false);
    const [mitarbeiterOptions, setMitarbeiterOptions] = useState<ProjektmitarbeiterItem[]>([]);
    const [memberSearchResults, setMemberSearchResults] = useState<ProjektmitarbeiterItem[]>([]);
    const memberSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [optionsLoaded, setOptionsLoaded] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Zahlungspläne management
    const [editedZahlungspläne, setEditedZahlungspläne] = useState<ZahlungsplanItem[]>([]);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerMode, setDrawerMode] = useState<'edit' | 'add' | null>(null);
    const [editingZahlungsplanIndex, setEditingZahlungsplanIndex] = useState<number | null>(null);
    const [editingRechnungsdatum, setEditingRechnungsdatum] = useState('');
    const [editingFaelligkeitsdatum, setEditingFaelligkeitsdatum] = useState('');
    const [editingNettobetrag, setEditingNettobetrag] = useState('');
    const [memberSearchInputValue, setMemberSearchInputValue] = useState('');

    // Centralized field permissions
    const allFieldPermissions = useMemo(() => {
        const permissions: Array<{ key: string; permissions: PermissionConfig }> = [
            // Cost Calculation Section
            { key: 'kostentraegerNummer', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Accounting': 'r', 'Rolle Controlling': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r' } },
            { key: 'stammKostentraegerNummer', permissions: { 'Rolle Administratoren': 'r', 'Rolle Accounting': 'r', 'Rolle Controlling': 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r' } },
            { key: 'kostenstellenNummer', permissions: { 'Rolle Administratoren': 'r', 'Rolle Accounting': 'r', 'Rolle Controlling': 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r' } },
            { key: 'kostenAusBMD', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Accounting': 'r', 'Rolle Controlling': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r' } },
            { key: 'ustPflichtig', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Accounting': 'r', 'Rolle Controlling': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r' } },
            { key: 'freigabeControlling', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r' } },
            { key: 'zustaendigerMaControlling', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Controlling': 'rw', 'Rolle Accounting': 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r' } },
            { key: 'ausfinanziertAsBoolean', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Accounting': 'r', 'Rolle Controlling': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r' } },
            // Financial Data - Contract Section (all read-only)
            { key: 'auftragsummeNetto', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Accounting': 'r', 'Rolle Controlling': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r' } },
            { key: 'foerdersumme', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Accounting': 'r', 'Rolle Controlling': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r' } },
            { key: 'eigenmittel', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Accounting': 'r', 'Rolle Controlling': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r' } },
            { key: 'foerderquoteProzent', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Accounting': 'r', 'Rolle Controlling': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r' } },
            { key: 'kommentarFinanzdaten', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Accounting': 'rw', 'Rolle Controlling': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r' } },
            // Financial Data - IMC Calculation Section (all read-only)
            { key: 'kostenPlan', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Accounting': 'r', 'Rolle Controlling': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r' } },
            { key: 'foerdersummeTotal', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Accounting': 'r', 'Rolle Controlling': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r' } },
            { key: 'deckungsbeitragPlan', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Accounting': 'r', 'Rolle Controlling': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r' } },
            { key: 'foerderquoteProzentPlan', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Accounting': 'r', 'Rolle Controlling': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r' } },
            // Financial Data - Consortium Section (all read-only)
            { key: 'finanzdatenKonsortiumVolumen', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Accounting': 'r', 'Rolle Controlling': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r' } },
            { key: 'finanzdatenKonsortiumDavonIMC', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Accounting': 'r', 'Rolle Controlling': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r' } },
            { key: 'finanzdatenKonsortiumIMCAnteil', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Accounting': 'r', 'Rolle Controlling': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r' } },
            // External FH Services Section (all read-only)
            { key: 'finanzleistungenErhalten', permissions: { 'Rolle Administratoren': 'r', 'Rolle Accounting': 'r', 'Rolle Controlling': 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r' } },
            { key: 'personalleistungenErhalten', permissions: { 'Rolle Administratoren': 'r', 'Rolle Accounting': 'r', 'Rolle Controlling': 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r' } },
            { key: 'sachleistungenErhalten', permissions: { 'Rolle Administratoren': 'r', 'Rolle Accounting': 'r', 'Rolle Controlling': 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r' } },
            // Payment Plan Section
            { key: 'zahlungsplan', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Accounting': 'r', 'Rolle Controlling': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Forschungsservice': 'rw', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', departmentHead: 'r' } },
        ];
        return permissions;
    }, []);

    // Check if user can edit any field
    const canEditAny = useMemo(() => {
        if (!project || !configUserInfo) return false;
        return allFieldPermissions.some((field) => {
            return canEditField(field.permissions, selectedRole, project, configUserInfo);
        });
    }, [project, selectedRole, configUserInfo, allFieldPermissions]);

    // Check if user can view any field (visibility check for entire tab)
    const canViewAny = useMemo(() => {
        if (!project || !configUserInfo) return selectedRole !== 'Benutzer';
        if (selectedRole === 'Benutzer') return false; // Regular users cannot see Financial tab
        return allFieldPermissions.some((field) => {
            return canViewField(field.permissions, selectedRole, project, configUserInfo);
        });
    }, [project, selectedRole, configUserInfo, allFieldPermissions]);

    // DataService instance
    const dataService = useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    useEffect(() => {
        setEditedValues(initializeEditedValues(project));
    }, [project]);

    // Fetch mitarbeiter when entering edit mode
    const fetchMitarbeiterOptions = useCallback(async () => {
        if (!dataService || optionsLoaded) return;

        try {
            const mitarbeiter = await dataService.getMitarbeiter();
            setMitarbeiterOptions(mitarbeiter);
            setMemberSearchResults(mitarbeiter);
            setOptionsLoaded(true);
        } catch (error) {
            console.error('Error fetching mitarbeiter:', error);
        }
    }, [dataService, optionsLoaded]);

    useEffect(() => {
        if (isEditing && !optionsLoaded && dataService) {
            fetchMitarbeiterOptions();
        }
    }, [isEditing, optionsLoaded, dataService, fetchMitarbeiterOptions]);

    // Handle member search for Controlling Staff Member
    const handleMemberSearch = useCallback((searchTerm: string) => {
        if (memberSearchTimeoutRef.current) clearTimeout(memberSearchTimeoutRef.current);

        if (searchTerm.length < 2) {
            setMemberSearchResults([]);
            return;
        }

        memberSearchTimeoutRef.current = setTimeout(() => {
            const filtered = mitarbeiterOptions.filter((member) => {
                const fullName = member.fullName || `${member.vorname} ${member.nachname}`;
                return fullName.toLowerCase().includes(searchTerm.toLowerCase());
            });
            setMemberSearchResults(filtered);
        }, 300);
    }, [mitarbeiterOptions]);

    // Initialize Zahlungspläne when entering/exiting edit mode
    useEffect(() => {
        if (isEditing && project?.zahlungsplan) {
            setEditedZahlungspläne([...project.zahlungsplan]);
        }
    }, [isEditing, project]);

    // Initialize member search input value when entering edit mode
    useEffect(() => {
        if (isEditing && mitarbeiterOptions.length > 0) {
            const currentMemberId = parseInt(editedValues.zustaendigerMaControlling as string);
            if (currentMemberId) {
                const member = mitarbeiterOptions.find((m) => m.personalID === currentMemberId);
                if (member) {
                    const fullName = member.fullName || `${member.vorname} ${member.nachname}`;
                    setMemberSearchInputValue(fullName);
                } else {
                    setMemberSearchInputValue('');
                }
            } else {
                setMemberSearchInputValue('');
            }
        } else if (!isEditing) {
            setMemberSearchInputValue('');
        }
    }, [isEditing, mitarbeiterOptions, editedValues.zustaendigerMaControlling]);

    // Zahlungspläne handlers
    const handleEditZahlungsplan = (index: number) => {
        const plan = editedZahlungspläne[index];
        if (plan) {
            setEditingZahlungsplanIndex(index);
            setEditingRechnungsdatum(plan.reDatum ? new Date(plan.reDatum).toISOString().split('T')[0] : '');
            setEditingFaelligkeitsdatum(plan.faelligkeitDatum ? new Date(plan.faelligkeitDatum).toISOString().split('T')[0] : '');
            setEditingNettobetrag(String(plan.betragNetto || 0));
            setDrawerMode('edit');
            setDrawerOpen(true);
        }
    };

    const handleOpenAddDrawer = () => {
        setEditingZahlungsplanIndex(null);
        setEditingRechnungsdatum('');
        setEditingFaelligkeitsdatum('');
        setEditingNettobetrag('');
        setDrawerMode('add');
        setDrawerOpen(true);
    };

    const handleSaveZahlungsplan = () => {
        if (!editingFaelligkeitsdatum || !editingNettobetrag || (drawerMode === 'add' && !editingRechnungsdatum)) {
            setErrorMessage('All fields are required');
            return;
        }

        if (drawerMode === 'edit' && editingZahlungsplanIndex !== null) {
            const updated = [...editedZahlungspläne];
            updated[editingZahlungsplanIndex] = {
                ...updated[editingZahlungsplanIndex],
                faelligkeitDatum: new Date(editingFaelligkeitsdatum),
                betragNetto: parseFloat(editingNettobetrag),
            };
            setEditedZahlungspläne(updated);
            setDrawerOpen(false);
            setDrawerMode(null);
            setEditingZahlungsplanIndex(null);
            setEditingRechnungsdatum('');
            setEditingFaelligkeitsdatum('');
            setEditingNettobetrag('');
        } else if (drawerMode === 'add') {
            const newEntry: ZahlungsplanItem = {
                guid: '00000000-0000-0000-0000-000000000000', // Assuming guid will be generated on the backend
                itemId: 0,
                departmentMetaId: 0,
                projektId: 0,
                reDatum: new Date(editingRechnungsdatum),
                faelligkeitDatum: new Date(editingFaelligkeitsdatum),
                betragNetto: parseFloat(editingNettobetrag),
                isRechnungErstellt: false,
                rechnungErstelltAsText: 'Nein',
                rechnungBezahltAsText: 'Nein',
            };
            setEditedZahlungspläne([...editedZahlungspläne, newEntry]);
            setDrawerOpen(false);
            setDrawerMode(null);
            setEditingRechnungsdatum('');
            setEditingFaelligkeitsdatum('');
            setEditingNettobetrag('');
        }
    };

    const handleRemoveZahlungsplan = (index: number) => {
        setEditedZahlungspläne((prev) => prev.filter((_, i) => i !== index));
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleFieldChange = (key: string, value: any) => {
        setEditedValues((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleSave = async () => {
        if (!project || !dataService) return;

        setIsSaving(true);
        try {
            const updatedProject: ProjektItem = {
                ...project,
                kostentraegerNummer: editedValues.kostentraegerNummer as string,
                stammKostentraegerNummer: editedValues.stammKostentraegerNummer as string,
                kostenstellenNummer: editedValues.kostenstellenNummer as string,
                ustPflichtig: editedValues.ustPflichtig as boolean,
                kostenAusBMD: editedValues.kostenAusBMD as boolean,
                ausfinanziertAsBoolean: editedValues.ausfinanziertAsBoolean as boolean,
                ausfinanziert: editedValues.ausfinanziertAsBoolean ? 1 : 0,
                freigabeControlling: editedValues.freigabeControlling as boolean,
                zustaendigerMaControllingId: (editedValues.zustaendigerMaControlling as string) ? parseInt(editedValues.zustaendigerMaControlling as string, 10) : undefined,
                auftragsummeNetto: editedValues.auftragsummeNetto ? Number(editedValues.auftragsummeNetto) : 0,
                foerdersumme: editedValues.foerdersumme ? Number(editedValues.foerdersumme) : 0,
                eigenmittel: editedValues.eigenmittel ? Number(editedValues.eigenmittel) : 0,
                foerderquoteProzent: editedValues.foerderquoteProzent ? Number(editedValues.foerderquoteProzent) : 0,
                kostenPlan: editedValues.kostenPlan ? Number(editedValues.kostenPlan) : 0,
                foerdersummeTotal: editedValues.foerdersummeTotal ? Number(editedValues.foerdersummeTotal) : 0,
                deckungsbeitragPlan: editedValues.deckungsbeitragPlan ? Number(editedValues.deckungsbeitragPlan) : 0,
                foerderquoteProzentPlan: editedValues.foerderquoteProzentPlan ? Number(editedValues.foerderquoteProzentPlan) : 0,
                finanzdatenKonsortiumVolumen: editedValues.finanzdatenKonsortiumVolumen ? Number(editedValues.finanzdatenKonsortiumVolumen) : 0,
                finanzdatenKonsortiumDavonIMC: editedValues.finanzdatenKonsortiumDavonIMC ? Number(editedValues.finanzdatenKonsortiumDavonIMC) : 0,
                finanzdatenKonsortiumIMCAnteil: editedValues.finanzdatenKonsortiumIMCAnteil ? Number(editedValues.finanzdatenKonsortiumIMCAnteil) : 0,
                kommentarFinanzdaten: editedValues.kommentarFinanzdaten as string,
                zahlungsplan: editedZahlungspläne || project.zahlungsplan || [],
            };

            const result = await dataService.updateProjekt(updatedProject);

            if (result.success) {
                if (refreshProject) {
                    await refreshProject();
                }
                setShowSuccessMessage(true);
                setErrorMessage(null);
                setTimeout(() => setShowSuccessMessage(false), 3000);
                setIsEditing(false);
            } else {
                setShowSuccessMessage(false);
                setErrorMessage(result.message);
                setTimeout(() => setErrorMessage(null), 5000);
            }
        } catch (error) {
            console.error('Error saving changes:', error);
            setShowSuccessMessage(false);
            setErrorMessage('Error saving. Please try again.');
            setTimeout(() => setErrorMessage(null), 5000);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        if (isSaving) return;
        setEditedValues(initializeEditedValues(project));
        setIsEditing(false);
        setMemberSearchResults([]);
        setMemberSearchInputValue('');
        if (memberSearchTimeoutRef.current) clearTimeout(memberSearchTimeoutRef.current);
    };

    const renderDisplayField = (config: FieldConfig) => {
        let displayValue = config.value;
        let valueContent: React.ReactNode = null;

        // Special case for Controlling Staff Member - show the text field
        if (config.key === 'zustaendigerMaControlling') {
            displayValue = project?.zustaendigerMaControlling || '';
        }

        if (displayValue === null || displayValue === undefined || displayValue === '') {
            valueContent = <div className={`${styles.fieldValue} ${styles.emptyValue}`}>-</div>;
        } else if (config.isBoolean) {
            const isTrue = displayValue === true || displayValue === 'Yes' || displayValue === 'true' || displayValue === 1;
            valueContent = (
                <div className={`${styles.booleanValue} ${isTrue ? styles.booleanTrue : styles.booleanFalse}`}>
                    {isTrue ? 'Yes' : 'No'}
                </div>
            );
        } else {
            valueContent = <div className={styles.fieldValue}>{displayValue}</div>;
        }

        return (
            <div className={styles.fieldBox}>
                <div className={styles.fieldLabel}>{config.label}</div>
                {valueContent}
            </div>
        );
    };

    const renderEditField = (config: FieldConfig) => {
        const value = String(editedValues[config.key] || '');

        if (config.isBoolean) {
            const isTrue = value === 'Yes' || value === 'true' || value === '1' || editedValues[config.key] === true;
            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{config.label}</div>
                    <div style={{ marginTop: '2px' }}>
                        <Checkbox
                            checked={isTrue}
                            onChange={(_ev, data) => {
                                handleFieldChange(config.key, data.checked);
                            }}
                        />
                    </div>
                </div>
            );
        }

        // Handle Combobox for Controlling Staff Member
        if (config.key === 'zustaendigerMaControlling') {
            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{config.label}</div>
                    <Combobox
                        placeholder="Enter at least 2 characters..."
                        value={memberSearchInputValue}
                        onOptionSelect={(_, option) => {
                            const memberId = option.optionValue || '';
                            const member = mitarbeiterOptions.find((m) => m.personalID === parseInt(memberId));
                            if (member) {
                                const fullName = member.fullName || `${member.vorname} ${member.nachname}`;
                                setMemberSearchInputValue(fullName);
                                handleFieldChange(config.key, memberId);
                            }
                        }}
                        onChange={(ev) => {
                            const searchValue = (ev.target as HTMLInputElement).value;
                            setMemberSearchInputValue(searchValue);
                            if (searchValue === '') {
                                handleFieldChange(config.key, '');
                            }
                            handleMemberSearch(searchValue);
                        }}
                        appearance="outline"
                    >
                        {memberSearchResults.map((member) => {
                            const fullName = member.fullName || `${member.vorname} ${member.nachname}`;
                            return (
                                <Option
                                    key={member.personalID}
                                    value={member.personalID.toString()}
                                    text={fullName}
                                >
                                    {fullName}
                                </Option>
                            );
                        })}
                    </Combobox>
                </div>
            );
        }

        if (config.isLargeTextarea) {
            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{config.label}</div>
                    <Textarea
                        value={value}
                        onChange={(_ev, data) => {
                            handleFieldChange(config.key, data.value);
                        }}
                        className={styles.largeTextarea}
                        resize="vertical"
                        textarea={{
                            style: {
                                minHeight: '120px',
                                maxHeight: 'none',
                            },
                        }}
                    />
                </div>
            );
        }

        if (config.isTextarea) {
            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{config.label}</div>
                    <Textarea
                        value={value}
                        onChange={(_ev, data) => {
                            handleFieldChange(config.key, data.value);
                        }}
                        className={styles.inputField}
                    />
                </div>
            );
        }

        // Handle currency fields
        const currencyFields = ['auftragsummeNetto', 'foerdersumme', 'eigenmittel', 'kostenPlan', 'deckungsbeitragPlan', 'foerdersummeTotal', 'finanzdatenKonsortiumVolumen', 'finanzdatenKonsortiumDavonIMC'];
        if (currencyFields.includes(config.key)) {
            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{config.label}</div>
                    <Input
                        type="number"
                        step="0.01"
                        value={value}
                        onChange={(_ev, data) => {
                            handleFieldChange(config.key, data.value);
                        }}
                        placeholder="0,00 €"
                        className={styles.inputField}
                    />
                </div>
            );
        }

        // Handle percentage fields
        const percentageFields = ['foerderquoteProzent', 'foerderquoteProzentPlan', 'finanzdatenKonsortiumIMCAnteil'];
        if (percentageFields.includes(config.key)) {
            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{config.label}</div>
                    <Input
                        type="number"
                        step="0.01"
                        value={value}
                        onChange={(_ev, data) => {
                            handleFieldChange(config.key, data.value);
                        }}
                        placeholder="0.00%"
                        className={styles.inputField}
                    />
                </div>
            );
        }

        return (
            <div className={styles.fieldBox}>
                <div className={styles.fieldLabel}>{config.label}</div>
                <Input
                    value={value}
                    onChange={(_ev, data) => {
                        handleFieldChange(config.key, data.value);
                    }}
                    className={styles.inputField}
                />
            </div>
        );
    };

    const renderField = (config: FieldConfig) => {
        // Look up field permissions from centralized array
        const fieldPermConfig = allFieldPermissions.find((f) => f.key === config.key);
        const fieldPermissions = fieldPermConfig?.permissions;
        const isFieldEditable = fieldPermissions ? canEditField(fieldPermissions, selectedRole, project, configUserInfo) : false;
        const isFieldVisible = fieldPermissions ? canViewField(fieldPermissions, selectedRole, project, configUserInfo) : true;

        if (!isFieldVisible) return null;
        if (isFieldEditable && isEditing && config.editable !== false) {
            return renderEditField(config);
        }
        return renderDisplayField(config);
    };

    const formatCurrency = (value: number | null | undefined): string => {
        if (value === null || value === undefined) return '-';
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
        }).format(value);
    };

    const formatPercentage = (value: number | null | undefined): string => {
        if (value === null || value === undefined) return '-';
        return `${value.toFixed(2)}%`;
    };

    if (!project) {
        return (
            <div className={styles.container}>
                <div className={styles.placeholder}>
                    <p>No project data available</p>
                </div>
            </div>
        );
    }

    // Check if user can view this section
    if (!canViewAny) {
        return (
            <div className={styles.container}>
                <div className={styles.placeholder}>
                    <p>You do not have permission to view financial data</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            {isSaving && (
                <MessageBar>
                    <MessageBarBody>
                        <MessageBarTitle>Saving...</MessageBarTitle>
                        Please wait while your changes are being saved.
                    </MessageBarBody>
                </MessageBar>
            )}
            {showSuccessMessage && (
                <MessageBar intent="success">
                    <MessageBarBody>
                        <MessageBarTitle>Success</MessageBarTitle>
                        Financial data saved successfully!
                    </MessageBarBody>
                </MessageBar>
            )}
            {errorMessage && (
                <MessageBar intent="error">
                    <MessageBarBody>
                        <MessageBarTitle>Error</MessageBarTitle>
                        {errorMessage}
                    </MessageBarBody>
                </MessageBar>
            )}
            {/* Header with Edit Button */}
            {canEditAny && (
                <div className={styles.header}>
                    <div className={styles.headerTitle}>Financial Data</div>
                    <div>
                        {isEditing ? (
                            <div style={{ display: 'flex', gap: tokens.spacingHorizontalM }}>
                                <Button
                                    appearance="primary"
                                    icon={<Save24Regular />}
                                    onClick={handleSave}
                                    disabled={isSaving}
                                >
                                    {isSaving ? 'Saving...' : 'Save'}
                                </Button>
                                <Button
                                    appearance="secondary"
                                    icon={<Dismiss24Regular />}
                                    onClick={handleCancel}
                                    disabled={isSaving}
                                >
                                    Cancel
                                </Button>
                            </div>
                        ) : (
                            <Button
                                appearance="outline"
                                icon={<Edit24Regular />}
                                onClick={() => setIsEditing(true)}
                            >
                                Edit
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {/* Cost Calculation Section */}
            <div className={styles.categorySection}>
                <div className={styles.categoryHeader}>Cost Calculation</div>
                <div className={styles.fieldsContainer}>
                    {renderField({ key: 'kostentraegerNummer', label: 'Cost Carrier Number', value: editedValues.kostentraegerNummer, editable: true })}
                    {renderField({ key: 'stammKostentraegerNummer', label: 'Base Cost Carrier Number', value: editedValues.stammKostentraegerNummer, editable: true })}
                    {renderField({ key: 'kostenstellenNummer', label: 'Cost Center Number', value: editedValues.kostenstellenNummer, editable: true })}
                    {renderField({ key: 'kostenAusBMD', label: 'Costs from BMD', value: project.kostenAusBMD, isBoolean: true, editable: true })}
                    {renderField({ key: 'ustPflichtig', label: 'VAT Liable', value: project.ustPflichtig, isBoolean: true, editable: true })}
                    {renderField({ key: 'freigabeControlling', label: 'Approval by Controlling', value: project.freigabeControlling, isBoolean: true, editable: true })}
                    {renderField({ key: 'zustaendigerMaControlling', label: 'Controlling Staff Member', value: editedValues.zustaendigerMaControlling, editable: true })}
                    {renderField({ key: 'ausfinanziertAsBoolean', label: 'Fully Financed', value: project.ausfinanziertAsBoolean, isBoolean: true, editable: true })}
                </div>
            </div>

            {/* Financial Data Contract Section */}
            <div className={styles.categorySection}>
                <div className={styles.categoryHeader}>Financial Data - Contract</div>
                <div className={styles.fieldsContainer}>
                    {renderField({ key: 'auftragsummeNetto', label: 'Max Eligible Costs per Contract', value: formatCurrency(project.auftragsummeNetto), editable: true })}
                    {renderField({ key: 'foerdersumme', label: 'Subsidy/Financing Amount per Contract', value: formatCurrency(project.foerdersumme), editable: true })}
                    {renderField({ key: 'eigenmittel', label: 'Own Funds', value: formatCurrency(project.eigenmittel), editable: true })}
                    {renderField({ key: 'foerderquoteProzent', label: 'Funding Rate per Contract', value: formatPercentage(project.foerderquoteProzent), editable: true })}
                    {renderField({ key: 'kommentarFinanzdaten', label: 'Comment', value: editedValues.kommentarFinanzdaten, isLargeTextarea: true, editable: true })}
                </div>
            </div>

            {/* Financial Data IMC Calculation Section */}
            <div className={styles.categorySection}>
                <div className={styles.categoryHeader}>Financial Data - IMC Calculation</div>
                <div className={styles.fieldsContainer}>
                    {renderField({ key: 'kostenPlan', label: 'Total Costs (incl. Overhead)', value: formatCurrency(project.kostenPlan), editable: true })}
                    {renderField({ key: 'foerdersummeTotal', label: 'Subsidy/Financing Amount per Contract', value: formatCurrency(project.foerdersummeTotal), editable: true })}
                    {renderField({ key: 'deckungsbeitragPlan', label: 'Planned Project Result', value: formatCurrency(project.deckungsbeitragPlan), editable: true })}
                    {renderField({ key: 'foerderquoteProzentPlan', label: 'Planned Funding Rate', value: formatPercentage(project.foerderquoteProzentPlan), editable: true })}
                </div>
            </div>

            {/* Consortium Financial Data Section */}
            <div className={styles.categorySection}>
                <div className={styles.categoryHeader}>Financial Data - Consortium</div>
                <div className={styles.fieldsContainer}>
                    {renderField({ key: 'finanzdatenKonsortiumVolumen', label: 'Consortium Volume', value: formatCurrency(project.finanzdatenKonsortiumVolumen), editable: true })}
                    {renderField({ key: 'finanzdatenKonsortiumDavonIMC', label: 'Thereof IMC', value: formatCurrency(project.finanzdatenKonsortiumDavonIMC), editable: true })}
                    {renderField({ key: 'finanzdatenKonsortiumIMCAnteil', label: 'IMC Share', value: formatPercentage(project.finanzdatenKonsortiumIMCAnteil), editable: true })}
                </div>
            </div>

            {/* External FH Services Section */}
            <div className={styles.categorySection}>
                <div className={styles.categoryHeader}>IMC External Services from Project Partners</div>
                <div className={styles.fieldsContainer}>
                    {renderField({ key: 'finanzleistungenErhalten', label: 'External IMC Financial Services / Received Subsidy', value: formatCurrency(project.finanzleistungenErhalten), editable: false })}
                    {renderField({ key: 'personalleistungenErhalten', label: 'External IMC Personnel Services', value: formatCurrency(project.personalleistungenErhalten), editable: false })}
                    {renderField({ key: 'sachleistungenErhalten', label: 'External IMC Material Services', value: formatCurrency(project.sachleistungenErhalten), editable: false })}
                </div>
            </div>

            {/* Payment Plan Section */}
            {(isEditing ? editedZahlungspläne : project.zahlungsplan) && (isEditing ? editedZahlungspläne : project.zahlungsplan).length > 0 && (
                <div className={styles.categorySection}>
                    <div className={styles.categoryHeader}>Payment Plan</div>
                    <div style={{ width: '50%' }}>
                        <div className={styles.tableContainer}>
                            <Table className={styles.table}>
                                <TableHeader className={styles.tableHeaderRow}>
                                    <TableRow>
                                        {isEditing && <TableHeaderCell style={{ width: '80px' }}>Actions</TableHeaderCell>}
                                        <TableHeaderCell>Invoice Date</TableHeaderCell>
                                        <TableHeaderCell>Due Date</TableHeaderCell>
                                        <TableHeaderCell>Net Amount</TableHeaderCell>
                                        <TableHeaderCell>Invoice</TableHeaderCell>
                                        <TableHeaderCell>Paid</TableHeaderCell>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {(isEditing ? editedZahlungspläne : project.zahlungsplan).map((plan, index) => (
                                        <TableRow key={index} onClick={isEditing ? () => handleEditZahlungsplan(index) : undefined} style={isEditing ? { cursor: 'pointer' } : {}}>
                                            {isEditing && (
                                                <TableCell>
                                                    <Button
                                                        icon={<Delete24Regular />}
                                                        appearance="subtle"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRemoveZahlungsplan(index);
                                                        }}
                                                        style={{ color: tokens.colorStatusDangerForeground1 }}
                                                    />
                                                </TableCell>
                                            )}
                                            <TableCell>
                                                {plan.reDatum ? new Date(plan.reDatum).toLocaleDateString('de-DE') : '-'}
                                            </TableCell>
                                            <TableCell>
                                                {plan.faelligkeitDatum ? new Date(plan.faelligkeitDatum).toLocaleDateString('de-DE') : '-'}
                                            </TableCell>
                                            <TableCell>{formatCurrency(plan.betragNetto)}</TableCell>
                                            <TableCell>{plan.rechnungErstelltAsText || '-'}</TableCell>
                                            <TableCell>{plan.rechnungBezahltAsText || '-'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                        {isEditing && (
                            <Button
                                appearance="primary"
                                onClick={handleOpenAddDrawer}
                                style={{ marginTop: tokens.spacingVerticalM }}
                            >
                                Add Payment Plan
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {/* Zahlungspläne Drawer */}
            {isEditing && (
                <Drawer open={drawerOpen} size="medium" position="end">
                    <DrawerHeader>
                        <DrawerHeaderTitle>{drawerMode === 'edit' ? 'Edit Payment Plan' : 'Add Payment Plan'}</DrawerHeaderTitle>
                    </DrawerHeader>
                    <DrawerBody>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                <label style={{ width: '120px', fontWeight: 500 }}>Invoice Date</label>
                                {drawerMode === 'edit' ? (
                                    <Input
                                        type="date"
                                        value={editingRechnungsdatum}
                                        disabled
                                        style={{ flex: 1 }}
                                    />
                                ) : (
                                    <Input
                                        type="date"
                                        value={editingRechnungsdatum}
                                        onChange={(e) => setEditingRechnungsdatum(e.target.value)}
                                        style={{ flex: 1 }}
                                    />
                                )}
                            </div>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                <label style={{ width: '120px', fontWeight: 500 }}>Due Date</label>
                                <Input
                                    type="date"
                                    value={editingFaelligkeitsdatum}
                                    onChange={(e) => setEditingFaelligkeitsdatum(e.target.value)}
                                    style={{ flex: 1 }}
                                />
                            </div>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                <label style={{ width: '120px', fontWeight: 500 }}>Net Amount</label>
                                <Input
                                    type="number"
                                    value={editingNettobetrag}
                                    onChange={(e) => setEditingNettobetrag(e.target.value)}
                                    placeholder="0.00"
                                    step="0.01"
                                    style={{ flex: 1 }}
                                />
                            </div>
                            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                                <Button
                                    appearance="primary"
                                    onClick={handleSaveZahlungsplan}
                                    disabled={
                                        (drawerMode === 'edit' && (!editingFaelligkeitsdatum || !editingNettobetrag)) ||
                                        (drawerMode === 'add' && (!editingRechnungsdatum || !editingFaelligkeitsdatum || !editingNettobetrag))
                                    }
                                >
                                    Save
                                </Button>
                                <Button
                                    appearance="secondary"
                                    onClick={() => {
                                        setDrawerOpen(false);
                                        setDrawerMode(null);
                                        setEditingZahlungsplanIndex(null);
                                        setEditingRechnungsdatum('');
                                        setEditingFaelligkeitsdatum('');
                                        setEditingNettobetrag('');
                                    }}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </DrawerBody>
                </Drawer>
            )}

            {/* Footer with Save Button (for non-edit mode) */}
            {canEditAny && isEditing && (
                <div className={styles.footerBar}>
                    <Button
                        appearance="primary"
                        icon={<Save24Regular />}
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        Save
                    </Button>
                    <Button
                        appearance="secondary"
                        icon={<Dismiss24Regular />}
                        onClick={handleCancel}
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>
                </div>
            )}
        </div>
    );
};

export default EckdatenFinanzielles;

